import { Filter, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { usePathname } from "next/navigation";

import { Button } from "@/app/_module/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { type ReminderFilters } from "@/app/_module/types/reminders";

interface ReminderFiltersComponentProps {
  filters: ReminderFilters;
  onFiltersChange: (filters: ReminderFilters) => void;
  onClearFilters: () => void;
}

export const ReminderFiltersComponent = function({
  filters,
  onFiltersChange,
  onClearFilters,
}: ReminderFiltersComponentProps) {
  const hasActiveFilters =
    filters.entityType ||
    filters.status !== "all" ||
    filters.dateRange !== "all" ||
    filters.type ||
    filters.priority;

  // On the /m shell the five stacked selects ate most of a screen — start
  // COLLAPSED there and let the header row toggle the panel open. Desktop
  // keeps its always-open card (collapse only exists on the shell).
  const isMobileShell = !!usePathname()?.startsWith("/m");
  const [expanded, setExpanded] = useState(!isMobileShell);

  return (
    <div className="bg-white p-4 rounded-lg border space-y-4">
      <div
        className="flex items-center justify-between"
        role={isMobileShell ? "button" : undefined}
        onClick={isMobileShell ? () => setExpanded((e) => !e) : undefined}
      >
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <span className="font-medium">Filters</span>
          {isMobileShell && !!hasActiveFilters && !expanded && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
              active
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!!hasActiveFilters && (
            <Button
              onClick={(e) => { e.stopPropagation(); onClearFilters(); }}
              size="sm"
              variant="ghost"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
          {isMobileShell && (
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            />
          )}
        </div>
      </div>

      {expanded && (
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700" htmlFor="status">
            Status
          </label>
          <Select
            value={filters.status || "all"}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                status: value as ReminderFilters["status"],
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700" htmlFor="type">
            Type
          </label>
          <Select
            value={filters.type || "all-types"}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                type: value === "all-types" ? undefined : value,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-types">All types</SelectItem>
              <SelectItem value="appointment">Appointment</SelectItem>
              <SelectItem value="follow-up">Follow-up</SelectItem>
              <SelectItem value="task">Task</SelectItem>
              <SelectItem value="deadline">Deadline</SelectItem>
              <SelectItem value="birthday">Birthday</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700" htmlFor="priority">
            Priority
          </label>
          <Select
            value={filters.priority || "all-priorities"}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                priority: value === "all-priorities" ? undefined : value,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-priorities">All priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700" htmlFor="entityType">
            Entity Type
          </label>
          <Select
            value={filters.entityType || "all-entities"}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                entityType: value === "all-entities" ? undefined : value,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All entity types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-entities">All entity types</SelectItem>
              <SelectItem value="Lead">Lead</SelectItem>
              <SelectItem value="Appointment">Appointment</SelectItem>
              <SelectItem value="Task">Task</SelectItem>
              <SelectItem value="Note">Note</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label
            className="text-sm font-medium text-gray-700"
            htmlFor="due_date"
          >
            Due Date
          </label>
          <Select
            value={filters.dateRange || "all"}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                dateRange: value as ReminderFilters["dateRange"],
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All dates</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="month">This month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      )}
    </div>
  );
}